import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button';
import Spinner from '../Spinner';
import { getAsset } from '../../../util/assets';
import { PURCHASED_EVENT_ADDON_FEATURES } from '../../../apps/pro/services/featurePermissions/helpers';
import useOpenModal from '../../../hooks/useOpenModal';
import useOpenAlert from '../../../hooks/useOpenAlert';
import useWaldoficationFeatureUpsell from '../WaldoficationFeatureUpsell/hooks/useWaldoficationFeatureUpsell';
import { formatPriceForDisplay } from '../../../util/format-money';
import { STRIPE_CHECKOUT_MODAL } from '../StripeCheckout';
import { ACCOUNT_REFERRAL_TYPE } from '../../../constants/account-referrals';
import { getErrorString } from '../../../util/normalize-errors';
import { EVENT_ADDON_ITEM_TYPE } from '../../../constants/event-items';
import useEventAddonPurchase from '../../../apps/pro/components/FreemiumEventPurchase/hooks/useEventAddonPurchase';

const propTypes = {
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string.isRequired,
  featureId: PropTypes.string.isRequired,
  onCheckoutSuccess: PropTypes.func,
};

const FEATURE_TITLES = {
  [PURCHASED_EVENT_ADDON_FEATURES.owned_content_free_joins_enabled]:
    'Upgrade your event to activate auto-tagging for EVERY guest!',
  [PURCHASED_EVENT_ADDON_FEATURES.custom_tags_enabled]:
    'Upgrade your event to unlock custom tags!',
  [PURCHASED_EVENT_ADDON_FEATURES.members_view_enabled]:
    'Upgrade your event to view and manage access to your event!',
  [PURCHASED_EVENT_ADDON_FEATURES.face_highlights_enabled]:
    'Upgrade your event to tag and find photos of every guest!',
  [PURCHASED_EVENT_ADDON_FEATURES.white_label_marketing_prints_enabled]:
    'Upgrade your event to remove watermark and add a personalized message to your marketing materials!',
};

const WaldoficationSimplifiedUpsell = ({
  albumId,
  albumFolderId,
  featureId,
  onCheckoutSuccess,
}) => {
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();

  const { eventAddonItems, account, loading } = useWaldoficationFeatureUpsell();

  const {
    onCheckout,
    submitting,
    selectedEventAddonItemIds,
  } = useEventAddonPurchase({
    albumId,
    albumFolderId,
    defaultSelectedEventAddonItemIds: (eventAddonItems ?? [])
      .filter(({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED)
      .map(({ id }) => id),
    onCheckoutError: ({ error }) => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    onCheckoutSuccess: () => {
      openAlert({
        type: 'success',
        title: 'Congratulations!',
        message:
          'Your event has been upgraded! You can now enjoy newly unlocked features!',
      });
      onCheckoutSuccess?.();
    },
  });

  const waldofiedAddon = (eventAddonItems ?? []).find(
    ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );

  const onPurchase = () => {
    openModal(STRIPE_CHECKOUT_MODAL, {
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_addon_items: [
            {
              album_folder_id: albumFolderId,
              event_addon_item_id: waldofiedAddon?.id,
            },
          ],
          promo_code: promoCode,
        };
      },
      accountReferralTypes: [ACCOUNT_REFERRAL_TYPE.EVENTS],
      onSuccess: token =>
        onCheckout({
          token,
          albumFolderId,
        }),
      defaultName: account?.name ?? '',
      defaultEmail: account?.email_address ?? '',
    });
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center w-100 h-100">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center">
      <img src={getAsset('/diamond-orange.svg')} alt="Diamond" />
      <h1 className="text-center">
        {FEATURE_TITLES[featureId] ?? 'Upgrade your event!'}
      </h1>
      <p className="text-dark text-center">
        Turn this into your best event yet with powerful features like
        auto-tagging for every guest, photo overlays and hashtags, and more!
      </p>
      <Button
        className="mt-1"
        onClick={onPurchase}
        disabled={submitting || !selectedEventAddonItemIds?.length}
      >
        {submitting ? (
          'Processing...'
        ) : (
          <Fragment>
            Upgrade now
            {waldofiedAddon
              ? ` ($${formatPriceForDisplay(waldofiedAddon.price.value)})`
              : ''}
          </Fragment>
        )}
      </Button>
    </div>
  );
};

WaldoficationSimplifiedUpsell.propTypes = propTypes;

export default WaldoficationSimplifiedUpsell;
